import { useEffect, useState } from 'react';
import type { ImageSource, JourneyNode } from '../../../core/entities/node';
import { formatTagList, parseTagInput, sanitizeTagInput } from '../../../core/policies/node-validation/tag-rules';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { AvatarPicker } from './AvatarPicker';
import { loadPresetAvatarCatalog, type PresetAvatarCatalog } from '../services/preset-avatar-loader';

export function NodeTypeForm({ node, onChange }: { node: JourneyNode; onChange: (node: JourneyNode) => void }) {
	const [catalog, setCatalog] = useState<PresetAvatarCatalog | null>(null);
	const [tagInput, setTagInput] = useState(() => formatTagList(node.tags));
	const { t } = useLocale();

	useEffect(() => {
		let active = true;

		void loadPresetAvatarCatalog().then((nextCatalog) => {
			if (active) {
				setCatalog(nextCatalog);
			}
		});

		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		setTagInput(formatTagList(node.tags));
	}, [node.nodeId]);

	function handleTagInputChange(value: string) {
		const nextTagInput = sanitizeTagInput(value);
		setTagInput(nextTagInput);
		onChange({ ...node, tags: parseTagInput(nextTagInput) });
	}

	function handleTagInputBlur() {
		const nextTags = parseTagInput(tagInput);
		setTagInput(formatTagList(nextTags));
		onChange({ ...node, tags: nextTags });
	}

	return (
		<section aria-label="Node core fields" style={{ display: 'grid', gap: 12 }}>
			<label style={{ display: 'grid', gap: 6 }}>
				<span>{t('displayName')}</span>
				<input className="hud-input" value={node.displayName} onChange={(event) => onChange({ ...node, displayName: event.target.value })} />
			</label>

			<label style={{ display: 'grid', gap: 6 }}>
				<span>{t('summaryLabel')}</span>
				<textarea className="hud-input" rows={3} value={node.summary ?? ''} onChange={(event) => onChange({ ...node, summary: event.target.value })} />
			</label>

			<label style={{ display: 'grid', gap: 6 }}>
				<span>{t('tags')}</span>
				<input className="hud-input" placeholder="math, research, wuxia" value={tagInput} onBlur={handleTagInputBlur} onChange={(event) => handleTagInputChange(event.target.value)} />
			</label>

			<label style={{ display: 'grid', gap: 6 }}>
				<span>{t('imageSource')}</span>
				<select className="hud-input" value={node.imageSource} onChange={(event) => onChange(setImageSource(node, event.target.value as ImageSource))}>
					<option value="fallback">{t('imageSourceFallback')}</option>
					<option value="preset-avatar">{t('imageSourcePresetAvatar')}</option>
					<option value="upload">{t('imageSourceUpload')}</option>
				</select>
			</label>

			{node.imageSource === 'preset-avatar' && (
				<>
					<label style={{ display: 'grid', gap: 6 }}>
						<span>{t('presetAvatarId')}</span>
						<input
							className="hud-input"
							placeholder="young-female-long-hair"
							value={node.presetAvatarId ?? ''}
							onChange={(event) => onChange({ ...node, imageSource: 'preset-avatar', presetAvatarId: event.target.value || null, imageAssetId: null })}
						/>
					</label>
					{catalog?.options.length ? (
						<div style={{ display: 'grid', gap: 6 }}>
							<span style={{ fontSize: 12, color: 'rgba(190, 214, 236, .68)' }}>{t('presetAvatarCatalog')}</span>
							<AvatarPicker
								options={catalog.options}
								selectedId={node.presetAvatarId}
								onSelect={(presetAvatarId) => onChange({ ...node, imageSource: 'preset-avatar', presetAvatarId, imageAssetId: null })}
							/>
						</div>
					) : null}
				</>
			)}

			{node.imageSource === 'upload' && (
				<label style={{ display: 'grid', gap: 6 }}>
					<span>{t('uploadedImageAssetId')}</span>
					<input
						className="hud-input"
						placeholder="asset-portrait-001"
						value={node.imageAssetId ?? ''}
						onChange={(event) => onChange({ ...node, imageSource: 'upload', imageAssetId: event.target.value || null, presetAvatarId: null })}
					/>
				</label>
			)}

			{node.type === 'Human' && (
				<>
					<ReferenceListField label={t('occupationIds')} placeholder={t('referenceOccupationPlaceholder')} values={node.occupationIds} onChange={(occupationIds) => onChange({ ...node, occupationIds })} />
					<ReferenceListField label={t('projectIds')} placeholder={t('referenceProjectPlaceholder')} values={node.projectIds} onChange={(projectIds) => onChange({ ...node, projectIds })} />
					<ReferenceListField label={t('relatedHumanIds')} placeholder={t('referenceHumanPlaceholder')} values={node.relatedHumanIds} onChange={(relatedHumanIds) => onChange({ ...node, relatedHumanIds })} />
				</>
			)}

			{node.type === 'Project' && (
				<>
					<ReferenceListField label={t('participantHumanIds')} placeholder={t('referenceHumanPlaceholder')} values={node.participantHumanIds} onChange={(participantHumanIds) => onChange({ ...node, participantHumanIds })} />
					<ReferenceListField label={t('relatedOccupationIds')} placeholder={t('referenceOccupationPlaceholder')} values={node.relatedOccupationIds} onChange={(relatedOccupationIds) => onChange({ ...node, relatedOccupationIds })} />
					<ReferenceListField label={t('relatedProjectIds')} placeholder={t('referenceProjectPlaceholder')} values={node.relatedProjectIds} onChange={(relatedProjectIds) => onChange({ ...node, relatedProjectIds })} />
				</>
			)}

			{node.type === 'Occupation' && (
				<>
					<ReferenceListField label={t('relatedHumanIds')} placeholder={t('referenceHumanPlaceholder')} values={node.relatedHumanIds} onChange={(relatedHumanIds) => onChange({ ...node, relatedHumanIds })} />
					<ReferenceListField label={t('typicalProjectIds')} placeholder={t('referenceProjectPlaceholder')} values={node.typicalProjectIds} onChange={(typicalProjectIds) => onChange({ ...node, typicalProjectIds })} />
				</>
			)}
		</section>
	);
}

function ReferenceListField({
	label,
	placeholder,
	values,
	onChange,
}: {
	label: string;
	placeholder: string;
	values: string[];
	onChange: (values: string[]) => void;
}) {
	const [inputValue, setInputValue] = useState(() => formatList(values));

	useEffect(() => {
		setInputValue(formatList(values));
	}, [values]);

	function handleChange(nextValue: string) {
		setInputValue(nextValue);
	}

	function handleBlur() {
		const normalizedValues = parseList(inputValue);
		setInputValue(formatList(normalizedValues));
		onChange(normalizedValues);
	}

	return (
		<label style={{ display: 'grid', gap: 6 }}>
			<span>{label}</span>
			<input className="hud-input" placeholder={placeholder} value={inputValue} onChange={(event) => handleChange(event.target.value)} onBlur={handleBlur} />
		</label>
	);
}

function parseList(value: string): string[] {
	return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function formatList(values: string[]): string {
	return values.join(', ');
}

function setImageSource(node: JourneyNode, imageSource: ImageSource): JourneyNode {
	if (imageSource === 'fallback') {
		return { ...node, imageSource, imageAssetId: null, presetAvatarId: null };
	}

	if (imageSource === 'preset-avatar') {
		return { ...node, imageSource, imageAssetId: null };
	}

	return { ...node, imageSource, presetAvatarId: null };
}
